"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { exportStretchedMasterKey, importStretchedMasterKey } from "./crypto"

const StretchedMasterKeyContext = createContext<{
  stretchedMasterKey: CryptoKey | null
  isInitializing: boolean
}>({ stretchedMasterKey: null, isInitializing: true })
const StoreStretchedMasterKeyContext = createContext<
  (storedMasterKey: StoredMasterKey) => Promise<void>
>(async () => {})
const PurgeStoredStretchedMasterKeyContext = createContext<() => void>(() => {})

/**
 * StretchedMasterKeyProvider is a normal react context provider, but it also persists the key to
 * session storage so that it persists after tab refreshes.
 */
export const StretchedMasterKeyProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [stretchedMasterKey, _setStretchedMasterKey] =
    useState<CryptoKey | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)

  const setStretchedMasterKey = useCallback(
    async (stretchedMasterKey: StoredMasterKey) => {
      _setStretchedMasterKey(stretchedMasterKey.key)
      await storeStretchedMasterKey(stretchedMasterKey)
    },
    [],
  )

  const purgeStoredStretchedMasterKey = useCallback(() => {
    _setStretchedMasterKey(null)
    sessionStorage.removeItem(STRETCHED_MASTER_KEY_KEY)
  }, [])

  useEffect(() => {
    async function load() {
      try {
        const storedKey = await getStoredMasterKey()
        if (storedKey) {
          _setStretchedMasterKey(storedKey.key)
        }
        setIsInitializing(false)
      } catch (error) {
        console.error(error)
      }
    }

    load()
  }, [])

  const value = {
    stretchedMasterKey,
    isInitializing,
  }

  return (
    <StretchedMasterKeyContext.Provider value={value}>
      <StoreStretchedMasterKeyContext.Provider value={setStretchedMasterKey}>
        <PurgeStoredStretchedMasterKeyContext.Provider
          value={purgeStoredStretchedMasterKey}
        >
          {children}
        </PurgeStoredStretchedMasterKeyContext.Provider>
      </StoreStretchedMasterKeyContext.Provider>
    </StretchedMasterKeyContext.Provider>
  )
}

export function useStretchedMasterKey() {
  const ctx = useContext(StretchedMasterKeyContext)
  if (!ctx) {
    throw new Error(
      "useStretchedMasterKey must be used within a StretchedMasterKeyProvider",
    )
  }
  return ctx
}
export function useStoreStretchedMasterKey() {
  const ctx = useContext(StoreStretchedMasterKeyContext)
  if (!ctx) {
    throw new Error(
      "useStoreStretchedMasterKey must be used within a StretchedMasterKeyProvider",
    )
  }
  return ctx
}
export function usePurgeStoredStretchedMasterKey() {
  const ctx = useContext(PurgeStoredStretchedMasterKeyContext)
  if (!ctx) {
    throw new Error(
      "usePurgeStoredStretchedMasterKey must be used within a StretchedMasterKeyProvider",
    )
  }
  return ctx
}

// master key storage utilities
const STRETCHED_MASTER_KEY_KEY = "social_signal_stretched_master_key"

export interface StoredMasterKey {
  key: CryptoKey
  /**
   * expiresAt shows when the key should be purged from the session storage.
   */
  expiresAt: Date
}

async function getStoredMasterKey(): Promise<StoredMasterKey | null> {
  if (typeof window === "undefined") return null

  try {
    const stored = sessionStorage.getItem(STRETCHED_MASTER_KEY_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored)
    // Check if session is expired
    if (new Date(parsed.expiresAt) <= new Date()) {
      sessionStorage.removeItem(STRETCHED_MASTER_KEY_KEY)
      return null
    }

    const key = await importStretchedMasterKey(parsed.key)

    return {
      key,
      expiresAt: new Date(parsed.expiresAt),
    }
  } catch (error) {
    console.error(error)
    sessionStorage.removeItem(STRETCHED_MASTER_KEY_KEY)
    return null
  }
}

async function storeStretchedMasterKey(stretchedMasterKey: StoredMasterKey) {
  if (typeof window === "undefined") return

  sessionStorage.setItem(
    STRETCHED_MASTER_KEY_KEY,
    JSON.stringify({
      key: await exportStretchedMasterKey(stretchedMasterKey.key),
      expiresAt: stretchedMasterKey.expiresAt.toISOString(),
    }),
  )
}
